// The signed-in person's organisation document, loaded once at sign-in
// (multi-tenant step 4): its features (features.js) and its settings — the
// values that used to be ASP constants in the code (rates, offices, lists).
// orgSetting(key, fallback) returns the setting, or the fallback when the org has
// not set it; ASP's settings were seeded with the old constants, so the fallback
// only ever shows for an org that has not set one.
import { doc, getDoc } from './fb/firestore.js';
import { db } from './firebase.js';
import { ORG_ID } from './config.js';
import { setFeatures, hasFeature } from './features.js';
import { CLIENT_SCHEMES, PROJECT_TYPES } from './ui.js';
import { setOffices } from './header.js';
import { setInfo, orgInfo } from './org-info.js';
import { setReportsAccess } from './nav.js';
import { setAssetsAccess } from './assets-access.js';
import { setFinanceFy27 } from './fin-pilot.js';

export { orgInfo, isContractor, setInfoFeature, setInfoLogo } from './org-info.js';

let settings = {};

// The last good FEATURES per org (not settings: they hold fee examples), so a
// failed read on a bad connection does not strip the rail to the core screens.
const KEEP = (id) => `track.org.${id}`;

export async function loadOrg() {
  let data = null;
  for (let i = 0; i < 2 && !data; i++) {
    try {
      const snap = await getDoc(doc(db, `organisations/${ORG_ID}`));
      data = (snap.exists() && snap.data()) || {};
    } catch (e) { console.warn('[org] could not load the organisation', e.message); }
  }
  if (data) {
    try {
      localStorage.setItem(KEEP(ORG_ID), JSON.stringify({ features: data.features || {}, name: data.name || '',
        template: data.template || '', branding: { logoPath: (data.branding && data.branding.logoPath) || '' } }));
    } catch (_) { /* private mode */ }
  } else {
    try { data = JSON.parse(localStorage.getItem(KEEP(ORG_ID)) || 'null'); } catch (_) { data = null; }
    data = data || {};
  }
  setFeatures(data.features || {});
  settings = data.settings || {};
  // One store for who the organisation is (org-info.js): the backend's
  // orgInfo() {name, template, branding} plus the shell's logo path and switches.
  setInfo(data);
  setReportsAccess(settings.reports);   // Reports (28/10/2026): who on this company has it
  setAssetsAccess(settings.assets);     // Assets (30/10/2026): settings.assets.access — empty = everyone
  setFinanceFy27(settings.features);    // Finance FY 2026–27 (01/11/2026): settings.features.financeFy27 — empty = nobody
  applyLists();
  setOffices(settings.offices);
  // World clocks are a company feature (Karan 21/10): on for ASP, off by default.
  const clocks = document.querySelector('.hdr-clocks');
  if (clocks) clocks.style.display = hasFeature('worldClocks') ? '' : 'none';
  applyBranding(data);
  return data;
}

// The org's own look (multi-tenant step 5): the tab title, and any brand
// colours it has set in branding.colors ({ orange, blue, 'dark-blue', navy } →
// --brand-*). ASP sets none, so ASP's palette is the stylesheet's, unchanged.
const COLOUR = /^#[0-9a-f]{3,8}$/i;
function applyBranding(data) {
  const b = (data && data.branding) || {};
  const name = (typeof b.appTitle === 'string' && b.appTitle) || (typeof data.name === 'string' && data.name ? `Track \u2014 ${data.name}` : 'Track');
  document.title = name;
  const colours = (b.colors && typeof b.colors === 'object') ? b.colors : {};
  for (const [k, v] of Object.entries(colours)) {
    if (/^[a-z0-9-]{2,24}$/.test(k) && typeof v === 'string' && COLOUR.test(v)) document.documentElement.style.setProperty(`--brand-${k}`, v);
  }
}

// The pick-lists the screens import from ui.js are the org's own when it has
// set them (settings.schemes / settings.projectTypes). The arrays are filled in
// place, so every importer sees this org's list without a code change.
function applyLists() {
  const fill = (arr, from) => {
    if (!Array.isArray(from) || !from.length || !from.every((x) => typeof x === 'string')) return;
    arr.splice(0, arr.length, ...from);
  };
  fill(CLIENT_SCHEMES, settings.schemes);
  fill(PROJECT_TYPES, settings.projectTypes);
}

/** A setting just saved from this browser (Catalogue › Fee agent): the screens read it without a reload. */
export function setOrgSettingLocal(key, value) { settings = { ...settings, [key]: value }; }

/** Every org setting as loaded (Settings search in ⌘K reads values from it). Read-only by convention. */
export function orgSettings() { return settings; }

export function orgSetting(key, fallback) {
  const v = settings[key];
  return v === undefined || v === null ? fallback : v;
}

/** The company's name as its people say it ("ASP"): the profile the
 *  AI prompts use (settings.profile.name), else the organisation's own name. */
export function orgName() {
  const profile = orgSetting('profile', null);
  return (profile && typeof profile.name === 'string' && profile.name) || orgInfo().name || '';
}

/** The company's mileage rate in pounds a mile (settings.mileageRate), or null
 *  when it has none — never a built-in figure (23/10/2026: no fixed 55p). */
export function mileageRate() {
  const r = Number(orgSetting('mileageRate', null));
  return Number.isFinite(r) && r > 0 ? r : null;
}
/** "45p" / "£1.10" for a rate in pounds. */
export const rateLabel = (r) => (r == null ? '' : r < 1 ? `${Math.round(r * 100)}p` : `£${r.toFixed(2)}`);
